import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import { Button, Input } from '../../ui';
import AuthService from '../services/AuthService';
import './ChangePasswordForm.css';

/**
 * ChangePasswordForm - Form for changing user password
 * @param {Object} props - Component props
 * @param {Function} props.onSuccess - Callback when password change is successful
 * @param {Function} props.onCancel - Callback when user cancels
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ChangePasswordForm component
 */
const ChangePasswordForm = ({ onSuccess, onCancel, className = '' }) => {
  const { t } = useTranslation();
  
  // Form state
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  /**
   * Handle input changes
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (error) setError(null);
  };


  /**
   * Validate form data
   */
  const validateForm = () => {
    const { currentPassword, newPassword, confirmPassword } = formData;

    if (!currentPassword.trim()) {
      setError(t('auth.changePassword.errors.currentPasswordRequired'));
      return false;
    }

    if (!newPassword.trim()) {
      setError(t('auth.changePassword.errors.newPasswordRequired'));
      return false;
    }

    if (newPassword.length < 8) {
      setError(t('auth.changePassword.errors.passwordTooShort'));
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.changePassword.errors.passwordsDoNotMatch'));
      return false;
    }

    if (currentPassword === newPassword) {
      setError(t('auth.changePassword.errors.samePassword'));
      return false;
    }

    return true;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await AuthService.changePassword(
        formData.currentPassword,
        formData.newPassword
      );

      if (error) {
        // Handle specific error messages from backend
        let errorMessage = error.message || t('auth.changePassword.errors.generic');
        
        // Map specific backend error messages to user-friendly translations
        if (error.message?.toLowerCase().includes('current password is incorrect')) {
          errorMessage = t('auth.changePassword.errors.currentPasswordIncorrect');
        } else if (error.message?.toLowerCase().includes('password too short')) {
          errorMessage = t('auth.changePassword.errors.passwordTooShort');
        } else if (error.message?.toLowerCase().includes('same password')) {
          errorMessage = t('auth.changePassword.errors.samePassword');
        }
        
        setError(errorMessage);
        return;
      }

      // Success
      setSuccess(true);
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

    } catch (err) {
      setError(t('auth.changePassword.errors.networkError'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError(null);
    setSuccess(false);
    if (onCancel) onCancel();
  };

  // Success state
  if (success) {
    return (
      <div className={`change-password-form ${className}`}>
        <h1 className="change-password-title">{t('auth.changePassword.success.title')}</h1>
        <div className="success-message">
          <div className="success-icon">
            <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="success-text">{t('auth.changePassword.success.message')}</p>
        </div>
        <div className="return-link">
          <button
            type="button"
            className="return-link-button"
            onClick={() => {
              if (onSuccess) onSuccess();
              handleCancel();
            }}
          >
            {t('auth.changePassword.returnToProfile')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`change-password-form ${className}`}>
      <h1 className="change-password-title">{t('auth.changePassword.title')}</h1>
      
      <form onSubmit={handleSubmit} className="change-password-form-content">
        {/* Current Password */}
        <div className="form-group">
          <Input
            type="password"
            label={t('auth.changePassword.currentPassword')}
            placeholder={t('auth.changePassword.currentPasswordPlaceholder')}
            value={formData.currentPassword}
            onChange={(e) => handleInputChange('currentPassword', e.target.value)}
            disabled={loading}
            required
            fullWidth
            showPassword
            autoComplete="current-password"
          />
        </div>

        {/* New Password */}
        <div className="form-group">
          <Input
            type="password"
            label={t('auth.changePassword.newPassword')}
            placeholder={t('auth.changePassword.newPasswordPlaceholder')}
            value={formData.newPassword}
            onChange={(e) => handleInputChange('newPassword', e.target.value)}
            disabled={loading}
            required
            fullWidth
            showPassword
            autoComplete="new-password"
          />
        </div>

        {/* Confirm New Password */}
        <div className="form-group">
          <Input
            type="password"
            label={t('auth.changePassword.confirmPassword')}
            placeholder={t('auth.changePassword.confirmPasswordPlaceholder')}
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            disabled={loading}
            required
            fullWidth
            showPassword
            autoComplete="new-password"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <div className="form-submit">
          <button
            type="submit"
            className="button-primary"
            disabled={loading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
          >
            {loading ? t('auth.changePassword.changing') : t('auth.changePassword.changePassword')}
          </button>
        </div>
      </form>

      {/* Return to Profile Link */}
      <div className="return-link">
        <button
          type="button"
          className="return-link-button"
          onClick={handleCancel}
        >
          {t('auth.changePassword.returnToProfile')}
        </button>
      </div>
    </div>
  );
};

ChangePasswordForm.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  className: PropTypes.string
};

export default ChangePasswordForm;
